/**
 * The editable form of a TimeRule (fixed time, or zman + offset + rounding),
 * shared by the minyan form and the opening-hours form.
 */
import type { DayType, PrayerType, RoundDirection, TimeRule } from '../types';
import { MYZMANIM_FIELDS } from '../config/constants';

/** Zmanim offered for "not later than" (Sof Zman Kriat Shema). */
export const LIMIT_ZMANIM = ['ShemaGra', 'ShemaMA'] as const;

/** Shacharis on these days can have "not later than" (Yoel, November 4, 2026: Shabbos and Yom Tov). */
export const LIMIT_DAY_TYPES: DayType[] = [
  'shabbos', 'yomTov', 'roshHashana', 'yomKippur', 'sukkotI', 'simchatTorah', 'pesachI', 'shviiShelPesach', 'shavuot',
];

/** Minyanim on these days can be "the same time all week" (Sunday to Thursday). */
export const WEEKLY_DAY_TYPES: DayType[] = ['weekday', 'sheniHamishi', 'beinHazmanim'];
export const WEEKLY_PRAYERS: PrayerType[] = ['shacharit', 'mincha', 'maariv'];

export function canLimit(prayer: PrayerType, dayTypes: DayType[]): boolean {
  return prayer === 'shacharit' && dayTypes.some((d) => LIMIT_DAY_TYPES.includes(d));
}

export function canBeWeekly(prayer: PrayerType, dayTypes: DayType[]): boolean {
  return WEEKLY_PRAYERS.includes(prayer) && dayTypes.some((d) => WEEKLY_DAY_TYPES.includes(d));
}

export const ROUND_TO_OPTIONS = [5, 10, 15, 30] as const;
export type RoundTo = (typeof ROUND_TO_OPTIONS)[number];
export const ROUND_DIRECTIONS: RoundDirection[] = ['up', 'down', 'nearest'];
export const ZMAN_KEYS = Object.keys(MYZMANIM_FIELDS);

export interface TimeRuleDraft {
  ruleType: 'fixed' | 'zmanOffset';
  fixedTime: string;
  baseZman: string;
  offsetMinutes: number;
  roundingEnabled: boolean;
  roundTo: RoundTo;
  roundDirection: RoundDirection;
  /** Zman rule: the same time Sunday to Thursday. */
  weekly: boolean;
  /** Fixed rule: "but not later than" Sof Zman Kriat Shema minus minutes, rounded down. */
  limitEnabled: boolean;
  limitZman: string;
  /** Minutes before the zman (a positive number; stored as minus). */
  limitMinutesBefore: number;
  limitRoundTo: RoundTo | 0;
  limitNote: string;
  limitNoteHe: string;
}

/** Form values for a rule; `defaultTime` is used when there is no fixed time yet. */
export function timeRuleToDraft(rule: TimeRule | undefined, defaultTime = '08:00'): TimeRuleDraft {
  return {
    ruleType: rule?.type ?? 'fixed',
    fixedTime: rule?.type === 'fixed' ? rule.time : defaultTime,
    baseZman: rule?.type === 'zmanOffset' ? rule.baseZman : ZMAN_KEYS[0],
    offsetMinutes: rule?.type === 'zmanOffset' ? rule.offsetMinutes : 0,
    roundingEnabled: rule?.type === 'zmanOffset' && !!rule.rounding,
    roundTo: rule?.type === 'zmanOffset' && rule.rounding ? rule.rounding.to : 5,
    roundDirection: rule?.type === 'zmanOffset' && rule.rounding ? rule.rounding.direction : 'up',
    weekly: rule?.type === 'zmanOffset' && !!rule.weekly,
    ...limitDraft(rule),
  };
}

function limitDraft(rule: TimeRule | undefined) {
  const l = rule?.type === 'fixed' ? rule.notLaterThan : undefined;
  return {
    limitEnabled: !!l,
    limitZman: l?.baseZman ?? LIMIT_ZMANIM[0],
    limitMinutesBefore: l ? -l.offsetMinutes : 40,
    limitRoundTo: (l ? l.rounding?.to ?? 0 : 5) as RoundTo | 0,
    limitNote: l?.note ?? '',
    limitNoteHe: l?.noteHe ?? '',
  };
}

/** The rule to store. Same result the minyan form always produced. */
export function draftToTimeRule(draft: TimeRuleDraft): TimeRule {
  if (draft.ruleType === 'fixed') {
    if (!draft.limitEnabled) return { type: 'fixed', time: draft.fixedTime };
    const note = draft.limitNote.trim();
    const noteHe = draft.limitNoteHe.trim();
    return {
      type: 'fixed',
      time: draft.fixedTime,
      notLaterThan: {
        baseZman: draft.limitZman,
        offsetMinutes: -Math.abs(Number(draft.limitMinutesBefore) || 0),
        ...(draft.limitRoundTo ? { rounding: { to: draft.limitRoundTo, direction: 'down' as const } } : {}),
        ...(note ? { note } : {}),
        ...(noteHe ? { noteHe } : {}),
      },
    };
  }
  return {
    type: 'zmanOffset',
    baseZman: draft.baseZman,
    offsetMinutes: Number(draft.offsetMinutes) || 0,
    ...(draft.roundingEnabled ? { rounding: { to: draft.roundTo, direction: draft.roundDirection } } : {}),
    ...(draft.weekly ? { weekly: true } : {}),
  };
}
